import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, CloudRain, CloudSnow, Sun, Cloud, CloudLightning, Wind, Activity, Lock, Search, Fuel } from 'lucide-react';
import api from '../api/client';
import './ServerOverview.css';
import { Championship } from '../types';
import RacingTyreIcon from './RacingTyreIcon';
import { formatCountdown, parseIsoDateTime } from '../utils/time';
import LiveDataModal from './LiveDataModal';
import type { LiveDataResponse } from './LiveDataModal';

interface ServerOverviewProps {
  onError?: (message: string) => void;
  onLoaded?: () => void;
  onSelectChampionship?: (championship: Championship) => void;
  broadcastPlatform?: 'youtube' | 'twitch' | '';
  broadcastChannel?: string;
  broadcastChannelId?: string;
  displayTimezone?: string;
}

interface ServerSummary {
  id: string;
  name: string;
  status: 'running' | 'stopped' | string;
  scheduledStart?: {
    enabled: boolean;
    dateTime: string;
    timezone: string;
  };
  serverConfig: {
    max_players: number;
  };
  seasonConfig: {
    event: {
      track: string;
      layout: string;
    };
    game_type?: string;
    game_config: {
      race_duration_type: string;
      race_duration: number;
      qualify_duration?: number | 0;
      practice_duration?: number | 0;
      warmup_duration?: number | 0;
      mandatory_pit_stop?: boolean;
      requires_tyre_change?: boolean;
      requires_refuelling?: boolean;
      pit_window?: number;
    };
    weather_type: string;
    weather_behaviour: string;
    initial_grip: string;
    allowed_cars_list_full: Array<{ car_name: string; display_name?: string }>;
  };
  currentPlayers?: number;
  currentSessionCode?: 'P' | 'Q' | 'W' | 'R' | null;
  progressPercent?: number;
  sessionLabel?: string;
  championshipId?: string;
  sessionEnded?: boolean;
  lastEndedSession?: {
    serverName: string;
    seasonConfig: ServerSummary['seasonConfig'];
    allowedCars: Array<{ car_name: string; display_name?: string }>;
    raceId?: string;
    championshipId?: string;
    endedAt: number;
  } | null;
}



const weatherIcon = (type: string) => {
  const key = String(type || '').toLowerCase();
  if (key.includes('clear')) return <Sun size={16} />;
  if (key.includes('storm') || key.includes('thunder') || key.includes('lightning')) return <CloudLightning size={16} />;
  if (key.includes('heavy_rain') || key.includes('rain') || key.includes('drizzle') || key.includes('damp')) return <CloudRain size={16} />;
  if (key.includes('snow')) return <CloudSnow size={16} />;
  if (key.includes('cloud') || key.includes('overcast')) return <Cloud size={16} />;
  if (key.includes('wind')) return <Wind size={16} />;
  return <Sun size={16} />;
};

const PitStopRules: React.FC<{ gameConfig: ServerSummary['seasonConfig']['game_config'] }> = ({ gameConfig }) => {
  if (!gameConfig.mandatory_pit_stop) return <>None</>;

  return (
    <span className="pit-stop-rules" aria-label="Mandatory pit stop">
      <span className={`pit-stop-icon ${gameConfig.requires_tyre_change ? '' : 'is-disabled'}`} title={gameConfig.requires_tyre_change ? 'Mandatory tyres' : 'No mandatory tyres'}>
        <RacingTyreIcon size={15} />
        {!gameConfig.requires_tyre_change && <span className="pit-stop-icon-strike" />}
      </span>
      <span className={`pit-stop-icon ${gameConfig.requires_refuelling ? '' : 'is-disabled'}`} title={gameConfig.requires_refuelling ? 'Mandatory fuel' : 'No mandatory fuel'}>
        <Fuel size={15} />
        {!gameConfig.requires_refuelling && <span className="pit-stop-icon-strike" />}
      </span>
      {typeof gameConfig.pit_window === 'number' && (
        <span className="pit-window-badge" title="Pit stop window">
          {Math.round(gameConfig.pit_window / 60)}m
        </span>
      )}
    </span>
  );
};

const formatGrip = (grip: string) => {
  const key = String(grip || '').toLowerCase();
  if (key.includes('optimum')) return 'Optimum';
  if (key.includes('fast')) return 'Fast';
  if (key.includes('green')) return 'Green';
  if (key.includes('_')) return grip.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return grip;
};

const formatDurationLabel = (durationSeconds: number, durationType: string) => {
  const isLaps = /laps/i.test(durationType || '');
  if (isLaps) {
    return `${durationSeconds} laps`;
  }

  const minutes = Math.max(1, Math.round(durationSeconds / 60));

  if (minutes > 99) {
    const hours = minutes / 60;
    return `${parseFloat(hours.toFixed(1))}h`;
  }

  return `${minutes} min`;
};

const formatCarName = (car: { car_name: string; display_name?: string }, catalog: Record<string, string>) => {
  const catalogName = car.car_name ? catalog[car.car_name] : '';
  if (catalogName) return catalogName;
  if (car.display_name) return car.display_name;
  const raw = String(car.car_name || '').trim();
  if (!raw) return 'Unknown car';
  const withoutPrefix = raw.replace(/^(ks[_-]|preset[_-]|acc[_-])/i, '');
  return withoutPrefix.split(/[_-]+/).map((w) => {
    if (!w) return '';
    if (/\d/.test(w) || w.length <= 2) return w.toUpperCase();
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
};

const getSessionLabel = (code?: 'P' | 'Q' | 'W' | 'R' | null) => {
  switch (code) {
    case 'P':
      return 'Practice';
    case 'Q':
      return 'Qualifying';
    case 'W':
      return 'Warmup';
    case 'R':
      return 'Race';
    default:
      return 'Weekend setup';
  }
};

const getWeekendSessions = (server: ServerSummary) => {
  const { game_config } = server.seasonConfig;
  const isPractice = server.seasonConfig.game_type === 'GameModeType_PRACTICE';
  const sessions: Array<{ id: string; code: 'P' | 'Q' | 'W' | 'R'; label: string; value: string }> = [];

  if (isPractice) {
    sessions.push({
      id: 'practice',
      code: 'P',
      label: 'Practice',
      value: formatDurationLabel(game_config.practice_duration || 0, 'time'),
    });
    return sessions;
  }

  if ((game_config.practice_duration || 0) > 0) {
    sessions.push({
      id: 'practice',
      code: 'P',
      label: 'Practice',
      value: formatDurationLabel(game_config.practice_duration || 0, 'time'),
    });
  }

  if ((game_config.qualify_duration || 0) > 0) {
    sessions.push({
      id: 'qualify',
      code: 'Q',
      label: 'Quali',
      value: formatDurationLabel(game_config.qualify_duration || 0, 'time'),
    });
  }

  if ((game_config.warmup_duration || 0) > 0) {
    sessions.push({
      id: 'warmup',
      code: 'W',
      label: 'Warmup',
      value: formatDurationLabel(game_config.warmup_duration || 0, 'time'),
    });
  }

  if ((game_config.race_duration || 0) > 0) {
    sessions.push({
      id: 'race',
      code: 'R',
      label: 'Race',
      value: formatDurationLabel(game_config.race_duration, game_config.race_duration_type || 'time'),
    });
  }

  return sessions;
};

const ServerOverview: React.FC<ServerOverviewProps> = ({ onError, onLoaded, onSelectChampionship, broadcastPlatform = '', broadcastChannel = '', broadcastChannelId = '', displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone }) => {
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [championships, setChampionships] = useState<Championship[]>([]);
  const [carsModalFor, setCarsModalFor] = useState<string | null>(null);
  const [liveModalFor, setLiveModalFor] = useState<string | null>(null);
  const [liveDataByServer, setLiveDataByServer] = useState<Record<string, LiveDataResponse>>({});
  const [endedLiveDataByServer, setEndedLiveDataByServer] = useState<Record<string, LiveDataResponse>>({});
  const [loadingLiveFor, setLoadingLiveFor] = useState<string | null>(null);
  const [carCatalog, setCarCatalog] = useState<Record<string, string>>({});
  const [carSearch, setCarSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);
  const [liveDataFetchedAt, setLiveDataFetchedAt] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchServers = async (showLoading = false) => {
      try {
        if (showLoading) setLoading(true);
        const res = await api.getServers();
        if (Array.isArray(res.data)) {
          setServers(res.data);
        } else if (showLoading) {
          setServers([]);
        }
      } catch (err) {
        console.error(err);
        if (showLoading) onError?.('Unable to load server overview.');
      } finally {
        if (showLoading) setLoading(false);
      }
    };

    const fetchCars = async () => {
      try {
        const res = await api.getCars();
        const entries = Array.isArray(res.data) ? res.data : [];
        const payload = entries.reduce<Record<string, string>>((acc, entry: any) => {
          const carName = entry?.car_name || entry?.name || '';
          const displayName = entry?.display_name || entry?.name || '';
          if (carName) {
            acc[carName] = displayName || carName;
          }
          return acc;
        }, {});
        setCarCatalog(payload);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchChampionships = async () => {
      try {
        const res = await api.getChampionships();
        setChampionships(Array.isArray(res.data?.championships) ? res.data.championships : []);
      } catch (err) {
        console.error(err);
      }
    };

    void fetchCars();
    void fetchChampionships();
    void fetchServers(true);

    const refreshInterval = setInterval(() => void fetchServers(false), 30_000);
    return () => clearInterval(refreshInterval);
  }, [onError]);

  useEffect(() => {
    if (!loading) onLoaded?.();
  }, [loading, onLoaded]);

  // Drive countdown re-renders every second
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Poll live telemetry every 5s while the modal is open (skip ended-session modals)
  useEffect(() => {
    if (!liveModalFor || liveModalFor.endsWith('-ended')) return;
    const serverId = liveModalFor;
    const poll = async () => {
      try {
        const res = await api.getServerLiveData(serverId);
        setLiveDataByServer((prev) => ({ ...prev, [serverId]: res.data }));
        recordLiveFetch(serverId);
      } catch (err) {
        console.error(err);
      }
    };
    const telemetryInterval = setInterval(() => void poll(), 5_000);
    return () => clearInterval(telemetryInterval);
  }, [liveModalFor]);

  const recordLiveFetch = (serverId: string) =>
    setLiveDataFetchedAt((prev) => ({ ...prev, [serverId]: Date.now() }));

  const liveServers = useMemo(
    () => servers.filter((server) => server.status === 'running'),
    [servers]
  );

  // Servers that have a frozen ended-session snapshot (still within 24h) — shown as a separate card
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const endedSessionServers = useMemo(
    () => servers.filter(
      (server) => server.lastEndedSession && (Date.now() - server.lastEndedSession.endedAt) < ONE_DAY_MS
    ),
    [servers]
  );

  const upcomingServers = useMemo(
    () => servers.filter(
      (server) => server.scheduledStart?.enabled && server.status !== 'running'
    ),
    [servers]
  );

  const openLiveData = async (server: ServerSummary) => {
    if (liveDataByServer[server.id]) {
      setLiveModalFor(server.id);
      return;
    }
    try {
      setLoadingLiveFor(server.id);
      const res = await api.getServerLiveData(server.id);
      setLiveDataByServer((prev) => ({ ...prev, [server.id]: res.data }));
      recordLiveFetch(server.id);
      setLiveModalFor(server.id);
    } catch (err) {
      console.error(err);
      onError?.('Unable to load live telemetry.');
    } finally {
      setLoadingLiveFor(null);
    }
  };

  const openEndedLiveData = async (server: ServerSummary) => {
    const key = `${server.id}-ended`;
    if (endedLiveDataByServer[key]) {
      setLiveModalFor(key);
      return;
    }
    try {
      setLoadingLiveFor(key);
      const res = await api.getServerEndedLiveData(server.id);
      setEndedLiveDataByServer((prev) => ({ ...prev, [key]: res.data }));
      setLiveModalFor(key);
    } catch (err) {
      console.error(err);
      onError?.('Unable to load ended session data.');
    } finally {
      setLoadingLiveFor(null);
    }
  };

  const handleChampionshipClick = async (server: ServerSummary) => {
    if (!server.championshipId) return;
    try {
      const res = await api.getChampionships();
      const championships = Array.isArray(res.data?.championships) ? res.data.championships : [];
      const championship = championships.find((entry: Championship) => entry.id === server.championshipId);
      if (championship) {
        onSelectChampionship?.(championship);
      } else {
        onError?.('Championship details could not be found.');
      }
    } catch (err) {
      console.error(err);
      onError?.('Unable to open the championship page.');
    }
  };

  const progressValue = (server: ServerSummary) => Math.min(100, Math.max(0, server.progressPercent ?? 0));

  const formatCountdownToDate = (value?: string) => {
    if (!value) return '—';
    const target = parseIsoDateTime(value);
    if (!target) return '—';
    return formatCountdown(target) === 'No upcoming race' ? '—' : formatCountdown(target);
  };

  const renderWeatherLabel = (server: ServerSummary) => {
    const behaviour = String(server.seasonConfig.weather_behaviour || 'STATIC')
      .replace(/^GameModeSelectionWeatherBehaviour_/i, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const type = String(server.seasonConfig.weather_type || 'CLEAR')
      .replace(/^GameModeSelectionWeatherType_/i, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

    return (
      <div className="weather-label">
        <span className="detail-kicker">{behaviour} Weather</span>
        <span className="detail-value">{weatherIcon(server.seasonConfig.weather_type)} {type}</span>
      </div>
    );
  };

  const renderEndedSessionCard = (server: ServerSummary) => {
    const snap = server.lastEndedSession;
    if (!snap) return null;
    const cardKey = `${server.id}-ended`;
    // Build a synthetic server-like object for helpers that need seasonConfig
    const snapServer: ServerSummary = {
      ...server,
      name: snap.serverName,
      seasonConfig: snap.seasonConfig,
      status: 'stopped',
      currentPlayers: 0,
      currentSessionCode: null,
      progressPercent: 0,
      sessionLabel: 'Ended',
      sessionEnded: true,
    };
    const sessions = getWeekendSessions(snapServer);
    const liveEntry = endedLiveDataByServer[cardKey];
    const isModalOpen = liveModalFor === cardKey;

    return (
      <React.Fragment key={cardKey}>
        <article className="server-card server-card--ended">
          <div className="card-accent" />
          <div className="server-card-top">
            <div className="server-name">{snap.seasonConfig.game_type === 'GameModeType_PRACTICE' ? 'Track Day' : 'Race Weekend'}</div>
            <div className="top-badges">
              {snap.championshipId && (
                <div className="championship-badge" title="Championship server">
                  <Lock size={13} />
                </div>
              )}
              <div className="status-pill status-pill--ended">Ended</div>
            </div>
            <div className="server-subtitle">{snap.serverName}</div>
          </div>

          <div className="session-grid">
            {sessions.map((session) => (
              <div key={session.id} className="session-card">
                <span className="session-card-label">{session.label}</span>
                <span className="session-card-value">{session.value}</span>
              </div>
            ))}
          </div>

          <div className="server-details">
            <div className="small-meta">
              <span className="detail-kicker">Track</span>
              {snap.seasonConfig.event.track} · {snap.seasonConfig.event.layout}
            </div>
            <div className="small-meta">
              <span className="detail-kicker">Ended</span>
              {new Date(snap.endedAt).toLocaleString(undefined, { timeZone: displayTimezone })}
            </div>
            {snap.seasonConfig.game_type !== 'GameModeType_PRACTICE' && (
              <div className="small-meta">
                <span className="detail-kicker">Pitstop</span>
                <PitStopRules gameConfig={snap.seasonConfig.game_config} />
              </div>
            )}
          </div>

          <div className="card-actions">
            <button type="button" className="button-secondary" onClick={() => void openEndedLiveData(server)}>
              <Activity size={14} /> Session data
            </button>
          </div>
        </article>
        {isModalOpen && (
          <LiveDataModal
            title="Session data"
            subtitle={`${snap.serverName} — ${snap.seasonConfig.event.track}`}
            sessions={sessions}
            liveData={liveEntry ?? null}
            isLoading={loadingLiveFor === cardKey}
            initialSessionCode={liveEntry?.sessionCode}
            isEnded
            endedAt={snap.endedAt}
            displayTimezone={displayTimezone}
            onClose={() => setLiveModalFor(null)}
          />
        )}
      </React.Fragment>
    );
  };

  const renderServerCard = (server: ServerSummary) => {
    const sessions = getWeekendSessions(server);
    const activeSessionCode = server.currentSessionCode ?? null;
    const activeSessionLabel = server.sessionLabel || getSessionLabel(activeSessionCode);
    const liveEntry = liveDataByServer[server.id];
    const isLive = server.status === 'running';
    const countdownValue = isLive
      ? (() => {
          if (!liveEntry || typeof liveEntry.timeLeftSeconds !== 'number') return '—';
          const elapsedSeconds = Math.floor((Date.now() - (liveDataFetchedAt[server.id] ?? Date.now())) / 1000);
          const remaining = Math.max(0, liveEntry.timeLeftSeconds - elapsedSeconds);
          const minutes = Math.floor(remaining / 60);
          const seconds = remaining % 60;
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        })()
      : formatCountdownToDate(server.scheduledStart?.dateTime);

    const filteredCars = (() => {
      const list = server.seasonConfig.allowed_cars_list_full || [];
      const trimmed = carSearch.trim().toLowerCase();
      if (!trimmed) return list;
      return list.filter((car) => {
        const label = `${formatCarName(car, carCatalog)} ${car.car_name || ''}`.toLowerCase();
        return label.includes(trimmed);
      });
    })();

    return (
      <article key={server.id} className="server-card">
        <div className="card-accent" />
        <div className="server-card-top">
          <div className="server-name">{server.seasonConfig.game_type === 'GameModeType_PRACTICE' ? 'Track Day' : 'Race Weekend'}</div>
          <div className="top-badges">
            {server.championshipId && (
              <div className="championship-badge" title="Championship server">
                <Lock size={13} />
              </div>
            )}
            <div className={`status-pill ${isLive && (server.currentPlayers ?? 0) > 0 ? 'is-live' : ''}`}>
              {isLive ? ((server.currentPlayers ?? 0) > 0 ? 'Live' : 'Idle') : 'Queued'}
            </div>
          </div>
          <div className="server-subtitle">{server.name}</div>
        </div>

        <div className="session-meta">
          <div className="session-meta-pill">
            <span className="session-pill-label">Players</span>
            <span className="players-bulb">
              <span className="players-bulb-dot" />
              {server.currentPlayers ?? 0} / {server.serverConfig.max_players}
            </span>
          </div>
          <div className="session-meta-pill">
            <span className="session-pill-label">{isLive ? 'Time Left' : 'Live In'}</span>
            <span>{countdownValue}</span>
          </div>
        </div>

        <div className="session-grid">
          {sessions.map((session) => (
            <div key={session.id} className={`session-card ${activeSessionCode === session.code ? 'is-active' : ''}`}>
              <span className="session-card-label">{session.label}</span>
              <span className="session-card-value">{session.value}</span>
              {/* {activeSessionCode === session.code && <span className="session-badge">Live</span>} */}
            </div>
          ))}
        </div>

        <div className="server-details">
          <div className="small-meta">
            <span className="detail-kicker">Track</span>
            {server.seasonConfig.event.track} · {server.seasonConfig.event.layout}
          </div>
          <div className="small-meta">
            <span className="detail-kicker">Grip</span>
            {formatGrip(server.seasonConfig.initial_grip)}
          </div>
          <div className="small-meta">
            {renderWeatherLabel(server)}
          </div>
          {server.seasonConfig.game_type !== 'GameModeType_PRACTICE' && (
            <div className="small-meta">
              <span className="detail-kicker">Pitstop</span>
              <PitStopRules gameConfig={server.seasonConfig.game_config} />
            </div>
          )}
        </div>

        <div className="progress-card">
          <div className="progress-head">
            <span>Progress</span>
            <span>{Math.round(progressValue(server))}%</span>
          </div>
          <div className="progress-wrap">
            <div className="progress-fill" style={{ width: `${progressValue(server)}%` }} />
          </div>
        </div>

        <div className="card-actions">
          <button type="button" className="button-secondary" onClick={() => void openLiveData(server)}>
            <Activity size={14} /> Live data
          </button>
          <button type="button" className="button-ghost" onClick={() => setCarsModalFor(server.id)}>
            <ChevronDown size={14} /> Cars
          </button>
          {server.championshipId && (
            <button type="button" className="button-ghost" onClick={() => void handleChampionshipClick(server)}>
              <Lock size={14} /> Sign Up
            </button>
          )}
        </div>

        {carsModalFor === server.id && (
          <div className="modal-backdrop" onClick={() => setCarsModalFor(null)}>
            <div className="modal-card" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h4>Allowed cars</h4>
                  <p>{server.seasonConfig.allowed_cars_list_full.length} cars available</p>
                </div>
                <button type="button" className="icon-button" onClick={() => setCarsModalFor(null)}>×</button>
              </div>
              <label className="search-field">
                <Search size={14} />
                <input
                  type="search"
                  value={carSearch}
                  onChange={(event) => setCarSearch(event.target.value)}
                  placeholder="Filter cars"
                />
              </label>
              <div className="car-list">
                {filteredCars.length === 0 && <p className="modal-empty">No cars match this search.</p>}
                {filteredCars.map((car) => (
                  <div key={`${car.car_name}-${car.display_name || ''}`} className="car-chip">
                    <span className="car-chip-dot" />
                    <span>{formatCarName(car, carCatalog)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {liveModalFor === server.id && (() => {
          const champ = championships.find((c) => c.id === server.championshipId);
          const canBroadcast = !!champ?.broadcasted && !!broadcastPlatform && !!(broadcastPlatform === 'youtube' ? broadcastChannelId : broadcastChannel);
          return (
            <LiveDataModal
              title="Live data"
              subtitle={liveEntry?.sessionLabel || activeSessionLabel}
              sessions={sessions}
              liveData={liveEntry ?? null}
              isLoading={loadingLiveFor === server.id}
              initialSessionCode={liveEntry?.sessionCode ?? server.currentSessionCode}
              canBroadcast={canBroadcast}
              broadcastPlatform={broadcastPlatform}
              broadcastChannel={broadcastChannel}
              broadcastChannelId={broadcastChannelId}
              onClose={() => setLiveModalFor(null)}
            />
          );
        })()}
      </article>
    );
  };

  return (
    <div className="server-overview">
      <section className="server-section">
        <h3>Live servers</h3>
        {loading && <p className="no-servers">Loading…</p>}
        {!loading && liveServers.length === 0 && <p className="no-servers">No live servers at the moment.</p>}
        {!loading && liveServers.length > 0 && (
          <div className="server-card-grid">
            {liveServers.map(renderServerCard)}
          </div>
        )}
      </section>

      {!loading && endedSessionServers.length > 0 && (
        <section className="server-section">
          <h3>Ended sessions</h3>
          <div className="server-card-grid">
            {endedSessionServers.map(renderEndedSessionCard)}
          </div>
        </section>
      )}

      <section className="server-section">
        <h3>Upcoming servers</h3>
        {loading && <p className="no-servers">Loading…</p>}
        {!loading && upcomingServers.length === 0 && <p className="no-servers">No upcoming servers.</p>}
        {!loading && upcomingServers.length > 0 && (
          <div className="server-card-grid">
            {upcomingServers.map(renderServerCard)}
          </div>
        )}
      </section>
    </div>
  );
};

export default ServerOverview;
